import BigNumber from "bignumber.js";
import Decimal from "decimal.js";

const DEFAULT_ZERO = "0";

class FormattedNumber extends BigNumber {
    constructor(value: unknown) {
        try {
            super(value as BigNumber.Value);
        } catch {
            super(0); // 如果父类构造函数出错，则使用 0 初始化
        }
    }
}

const isValidNumber = (value: unknown): value is number | string => {
    const typeOfValue = typeof value;
    if (!["string", "number"].includes(typeOfValue)) {
        return false;
    }

    if (
        typeof value === "number" &&
        (Number.isNaN(value) || !Number.isFinite(value))
    ) {
        return false;
    }

    if (
        typeof value === "string" &&
        (value.length === 0 || Number.isNaN(Number.parseFloat(value)))
    ) {
        return false;
    }

    return true;
};

const formatWithFixedDicemal = (number: number | BigNumber) => {
    const bigNumberValue =
        number instanceof BigNumber ? number : new FormattedNumber(number);

    return bigNumberValue
        .toFormat(2, BigNumber.ROUND_DOWN)
        .replace(/\.?0+$/, "");
};

const formatWithAbbrev = (number: number | BigNumber) => {
    const bigNumberValue =
        number instanceof BigNumber ? number : new FormattedNumber(number);

    const abbreviations = [
        { threshold: 1e12, suffix: "T" }, // 万亿
        { threshold: 1e9, suffix: "B" }, // 十亿
        { threshold: 1e6, suffix: "M" }, // 百万
        { threshold: 1e3, suffix: "K" }, // 千
    ];

    for (const abbreviation of abbreviations) {
        if (bigNumberValue.abs().gte(abbreviation.threshold)) {
            const num = bigNumberValue
                .dividedBy(abbreviation.threshold)
                .toFixed(2, BigNumber.ROUND_DOWN)
                .replace(/\.?0+$/, "");

            return num + abbreviation.suffix;
        }
    }

    return formatWithFixedDicemal(bigNumberValue);
};

export const formatNumber = (
    value: number | string,
    options?: { bigNumThreshold?: number; dicimalPlaces?: number }
): string => {
    if (!isValidNumber(value)) {
        return DEFAULT_ZERO;
    }

    if (typeof value === "string" && value.indexOf(",") !== -1) {
        value = value.replace(/,/g, "");
    }

    const bigNumberValue = new FormattedNumber(value as number | string);
    if (bigNumberValue.isNaN() || !bigNumberValue.isFinite()) {
        return DEFAULT_ZERO;
    }

    if (bigNumberValue.isZero()) {
        return DEFAULT_ZERO;
    }

    const absNum = bigNumberValue.abs();

    if (absNum.isLessThan(1e3)) {
        const subscripts = ["₀", "₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"];
        const [prefix, decimal] = bigNumberValue.toFixed().split(".");

        let zeros = 0;

        if (decimal && decimal.length > 0) {
            for (let i = 0; i < decimal.length; i++) {
                if (decimal[i] === "0") {
                    zeros++;
                } else {
                    break;
                }
            }
        }

        const decimalPlaces =
            typeof options?.dicimalPlaces === "number"
                ? options?.dicimalPlaces
                : absNum.isLessThan(10)
                  ? 6
                  : absNum.isLessThan(100)
                    ? 5
                    : 4;

        if (zeros < 3) {
            return bigNumberValue
                .toFixed(decimalPlaces, BigNumber.ROUND_DOWN)
                .replace(/\.?0+$/, "");
        }

        if (zeros < 10) {
            const result = `${prefix}.0${subscripts[zeros]}${decimal.slice(zeros, zeros + decimalPlaces - 2)}`;
            return result.replace(/0+$/, "");
        }

        if (zeros > 9 && zeros < 100) {
            const subscript =
                subscripts[Math.floor(zeros / 10)] + subscripts[zeros % 10];
            const result = `${prefix}.0${subscript}${decimal.slice(zeros, zeros + decimalPlaces - 3)}`;
            return result.replace(/0+$/, "");
        }

        const result = `${prefix}.0₉₉₊${decimal.slice(zeros, zeros + decimalPlaces - 4)}`;
        return result.replace(/0+$/, "");
    }

    const threshold = options?.bigNumThreshold ?? 1e3;
    if (bigNumberValue.isLessThan(threshold)) {
        return formatWithFixedDicemal(bigNumberValue);
    }

    return formatWithAbbrev(bigNumberValue);
};

/*
 * 格式化代币数量，超过千位按照缩写形式展示
 * @value 代币数量
 */
export const formatAmount = (value: unknown) => formatNumber(value);

/*
 * 格式化代币余额，超过百万按照缩写形式展示
 * @value 代币数量
 */
// export const formatBalance = (value: unknown) =>
//     formatNumber(value, { bigNumThreshold: 1e6 });

/*
 * 格式化代币价格，价格超过百万使用带单位的数字展示
 */
export const formatPrice = (value: unknown) =>
    `$${formatNumber(value, { bigNumThreshold: 1e9 })}`;

/**
 * 格式化代币市值
 */
export const formatMarketCap = (value: unknown) =>
    `$${formatNumber(value, { dicimalPlaces: 2 })}`;

/**
 * 格式化量能
 */
export const formatVolume = (value: unknown) =>
    `$${formatNumber(value, { dicimalPlaces: 2 })}`;

/**
 * 格式化百分比
 */
export const formatPercent = (
    value: number | string | null | undefined,
    ignoreSymbol: boolean = false,
    invalidDisplay: string = "--"
): string => {
    if (!isValidNumber(value)) {
        return invalidDisplay;
    }

    const number = Number(value);
    const symbol = ignoreSymbol ? "" : number > 0 ? "+" : "";
    const result = number >= 1e6 ? formatWithAbbrev(number) : number.toFixed(2);
    return `${symbol}${result}%`;
};

/**
 * 格式化数值为价值显示
 */
export const formatValue = (
    value: unknown,
    {
        len8 = false,
        prefix = "$",
        needFormat = true,
    }: { len8?: boolean; prefix?: string; needFormat?: boolean } = {}
): string => {
    const decimalValue = new Decimal(isNaN(Number(value)) ? 0 : Number(value));

    // 处理前缀
    const signPrefix = decimalValue.isNegative() ? `-${prefix}` : prefix;

    const absValue = decimalValue.abs();
    let result: string;

    if (absValue.gte(1)) {
        // 大于等于 1，保留两位小数，千分位格式化
        result = absValue
            .toFixed(len8 ? 2 : 2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else if (absValue.eq(0)) {
        // 等于 0
        result = "0.00";
    } else {
        // 小于 1，特殊格式化
        return `$${formatNumber(value, { dicimalPlaces: 4 })}`;
    }

    // 修正小数点后的冗余 0
    if (result.includes(".")) {
        const [integerPart, decimalPart] = result.split(".");
        const trimmedDecimalPart = decimalPart.replace(/0+$/, "");

        // 确保小数部分至少保留两位，即 "0.00"
        result =
            trimmedDecimalPart.length >= 2
                ? `${integerPart}.${trimmedDecimalPart}`
                : `${integerPart}.${trimmedDecimalPart.padEnd(2, "0")}`;
    } else {
        result = `${result}.00`; // 防止没有小数部分时补齐
    }

    const formattedValue = `${signPrefix}${result}`;

    // 返回结果，是否需要字符间插入零宽字符
    return needFormat
        ? formattedValue.split("").join("\u200B")
        : formattedValue;
};

/**
 * 格式化数值为余额显示
 */
export const formatBalance = (
    value: unknown,
    {
        len8 = false,
        delZero = false,
        needFormat = true,
    }: { len8?: boolean; delZero?: boolean; needFormat?: boolean } = {}
): string => {
    if (value == null || isNaN(Number(value))) {
        return "--";
    }

    const decimalValue = new Decimal(isNaN(Number(value)) ? 0 : Number(value));
    let result: string;

    if (decimalValue.gte(10)) {
        // 大于等于 10，保留两位小数
        const re = decimalValue.toDecimalPlaces(2, Decimal.ROUND_DOWN);
        result = formatWithCommas(re.toFixed(2));
    } else if (decimalValue.gte(1)) {
        // 大于等于 1 且小于 10，保留四位小数
        const re = decimalValue.toDecimalPlaces(4, Decimal.ROUND_DOWN);
        result = formatWithCommas(len8 ? re.toFixed(2) : re.toFixed(4));
    } else if (decimalValue.eq(0)) {
        result = "0.00";
    } else {
        // 小于 1，保留六到八位小数
        const scale = len8 ? 8 : 6;
        const re = decimalValue.toDecimalPlaces(scale, Decimal.ROUND_DOWN);
        result = formatWithCommas(re.toFixed(scale));
    }

    // 修正小数点后的冗余 0
    if (result.includes(".")) {
        const [integerPart, decimalPart] = result.split(".");
        const trimmedDecimalPart = decimalPart.replace(/0+$/, "");

        // 确保小数部分至少保留两位
        result =
            trimmedDecimalPart.length >= 2
                ? `${integerPart}.${trimmedDecimalPart}`
                : `${integerPart}.${trimmedDecimalPart.padEnd(2, "0")}`;
    }

    // 移除尾部的 ".00"（如果 `delZero` 为 true）
    if (delZero && result.endsWith(".00")) {
        result = result.slice(0, -3);
    }

    // 添加字符间隔（需要格式化时）
    return needFormat ? result.split("").join("\u200B") : result;
};

/**
 * 添加千分位格式化
 */
const formatWithCommas = (numStr: string): string => {
    const [integerPart, decimalPart] = numStr.split(".");
    return decimalPart
        ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + decimalPart
        : integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
